import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import Toggle from "@shared/v2/Toggle";
import Typography from "@shared/v2/Typography";
import Button from "@shared/v2/Button";
import TextInput from "@shared/v2/TextInput";
import Tag from "@shared/v2/Tag";
import Checkbox from "@shared/v2/Checkbox";
import Dropdown from "@shared/v2/Dropdown";
import Tooltip from "@shared/v2/Tooltip";
import { caseTransformingAxios } from "@shared/v2/caseTransformingAxios";
import { useDebounce } from "@shared/hookHelpers";
import { CircleInfoSolidV6 } from "@shared/v2/Icomoon";
import dayjs from "@shared/dayjs";
import axios from "axios";
import CollapsibleWebsiteCard from "../shared/CollapsibleWebsiteCard";
import { AREA_GROUPS_WITH_ADDRESS, AREA_SEARCH_MIN_CHARS } from "./areaSearch";

const FeaturedListingsPanel = ({ data = undefined, setFeaturedListingsData }) => {
  const [search, setSearch] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [roster, setRoster] = useState([]);
  const debouncedSearchTerm = useDebounce(searchTerm, 250);

  useEffect(() => {
    axios
      .get(`/website/agent_mls_ids`, {
        headers: {
          "x-csrf-token": ReactOnRails.authenticityToken(),
          "Content-Type": "application/json",
        },
      })
      .then((res) => {
        // agentIds filters on MLS agent IDs, so the option value must be the MLS ID, not the person ID.
        setRoster(
          res.data.map((a) => ({
            value: a.mls_agent_id,
            label: `${a.full_name} - ${a.mls_agent_id}${a.mls ? ` (${a.mls})` : ""}`,
          })),
        );
      });
  }, []);

  // Arrays toggle `value` as a member unless `replace` is set, which assigns `value` outright.
  const updateConfig = (path, value, { replace = false } = {}) => {
    setFeaturedListingsData((prev) => {
      const keys = path.split(".");

      const isEqual = (a, b) => {
        // Same reference or primitive equality
        if (a === b) {
          return true;
        }

        // One is null / undefined / not an object
        if (a === null || b === null || typeof a !== "object" || typeof b !== "object") {
          return false;
        }

        // Arrays
        if (Array.isArray(a) || Array.isArray(b)) {
          if (!Array.isArray(a) || !Array.isArray(b)) {
            return false;
          }

          if (a.length !== b.length) {
            return false;
          }

          return a.every((item, index) => isEqual(item, b[index]));
        }

        // Objects
        const aKeys = Object.keys(a);
        const bKeys = Object.keys(b);

        if (aKeys.length !== bKeys.length) {
          return false;
        }

        return aKeys.every((key) => Object.prototype.hasOwnProperty.call(b, key) && isEqual(a[key], b[key]));
      };

      const updateNested = (obj, index) => {
        const key = keys[index];

        if (index === keys.length - 1) {
          const currentValue = obj?.[key];

          // Array = toggle
          if (!replace && Array.isArray(currentValue)) {
            const exists = currentValue.some((item) => isEqual(item, value));

            return {
              ...obj,
              [key]: exists ? currentValue.filter((item) => !isEqual(item, value)) : [...currentValue, value],
            };
          }

          // Non-array = set
          return {
            ...obj,
            [key]: value,
          };
        }

        return {
          ...obj,
          [key]: updateNested(obj?.[key] ?? {}, index + 1),
        };
      };

      const binding = prev.bindings[0];

      return {
        ...prev,
        bindings: [
          {
            ...binding,
            config: {
              ...binding.config,
              vars: {
                ...binding.config.vars,
                requestBody: updateNested(binding.config.vars.requestBody, 0),
              },
            },
          },
        ],
      };
    });
  };

  const initialPage = {
    area: "",
    areas: [{ id: 1, name: "Barklay, Bellingham, WA" }],
    agentId: "",
    agentIds: ["24353425"],
    officeId: "",
    officeIds: [],
    propertyTypes: [],
    propertyStatus: [],
    priceMin: "",
    priceMax: "",
    beds: "",
    baths: "",
    sqft: "",
    garage: "",
    lotSize: "",
    yearBuilt: "",
    filterPriority: "",
    sortListingBy: "",
    onlyShowAgentOfficeListings: false,
  };
  const [page, setPage] = useState(initialPage);
  const [areaGroups, setAreaGroups] = useState([]);

  useEffect(() => {
    if (debouncedSearchTerm.length >= AREA_SEARCH_MIN_CHARS) {
      const params = {
        search_term: debouncedSearchTerm,
        search_type: "fsbos",
      };
      caseTransformingAxios.post("/website/autocomplete", params).then((res) => {
        const results = res.data.results || {};
        const result = AREA_GROUPS_WITH_ADDRESS.filter(
          ([key]) => Array.isArray(results[key]) && results[key].length,
        ).map(([key, label]) => ({
          label,
          options: results[key].map((item) => ({
            label: item.name,
            value: item.name,
            type: key,
          })),
        }));

        setAreaGroups(result);
      });
    } else {
      setAreaGroups([]);
    }
  }, [debouncedSearchTerm]);

  const propertyTypes = [
    { id: "Business Opportunity", label: "Business Opportunity" },
    { id: "Commercial Lease", label: "Commercial Lease" },
    { id: "Commercial Sale", label: "Commercial Sale" },
    { id: "Condo/Townhouse", label: "Condo/Townhouse" },
    { id: "Farm/Ranch", label: "Farm/Ranch" },
    { id: "Manufactured Home", label: "Manufactured Home" },
    { id: "Multi-family", label: "Multi-family" },
    { id: "Residential", label: "Residential" },
    { id: "Residential Lease", label: "Residential Lease" },
    { id: "Vacant Land", label: "Vacant Land" },
  ];

  const propertyStatus = [
    { id: 1, label: "Active" },
    { id: 2, label: "Pending" },
    { id: 3, label: "Active w/ Contingency" },
    { id: 4, label: "Sold" },
  ];

  const bedOptions = [
    { value: 0, label: "Any" },
    { value: 1, label: "1+" },
    { value: 2, label: "2+" },
    { value: 3, label: "3+" },
    { value: 4, label: "4+" },
    { value: 5, label: "5+" },
    { value: 6, label: "6+" },
    { value: 7, label: "7+" },
    { value: 8, label: "8+" },
    { value: 9, label: "9+" },
    { value: 10, label: "10+" },
  ];

  const bathOptions = [
    { value: 0, label: "Any" },
    { value: 1, label: "1+" },
    { value: 2, label: "2+" },
    { value: 3, label: "3+" },
    { value: 4, label: "4+" },
  ];

  const garageOptions = [
    { value: 0, label: "Any" },
    { value: 1, label: "1+" },
    { value: 2, label: "2+" },
    { value: 3, label: "3+" },
    { value: 4, label: "4+" },
  ];

  const sqftOptions = [
    { value: 0, label: "Any" },
    { value: 600, label: "600+" },
    { value: 800, label: "800+" },
    { value: 1000, label: "1000+" },
    { value: 1200, label: "1200+" },
    { value: 1400, label: "1400+" },
    { value: 1600, label: "1600+" },
    { value: 1800, label: "1800+" },
    { value: 2000, label: "2000+" },
    { value: 2250, label: "2250+" },
    { value: 2500, label: "2500+" },
    { value: 2750, label: "2750+" },
    { value: 3000, label: "3000+" },
    { value: 3500, label: "3500+" },
    { value: 4000, label: "4000+" },
    { value: 4500, label: "4500+" },
    { value: 5000, label: "5000+" },
  ];

  const lotOptions = [
    { value: 0, label: "Any" },
    { value: 0.1, label: ".1+" },
    { value: 0.2, label: ".2+" },
    { value: 0.25, label: ".25+" },
    { value: 0.33, label: ".33+" },
    { value: 0.5, label: ".5+" },
    { value: 0.75, label: ".75+" },
    { value: 1, label: "1+" },
    { value: 1.5, label: "1.5+" },
    { value: 2, label: "2+" },
    { value: 5, label: "5+" },
  ];

  const yearOptions = [
    { value: 0, label: "Any" },
    { value: 1800, label: "1800+" },
    { value: 1900, label: "1900+" },
    { value: 1970, label: "1970+" },
    { value: 1980, label: "1980+" },
    { value: 1990, label: "1990+" },
    { value: 2000, label: "2000+" },
    { value: 2005, label: "2005+" },
    { value: 2010, label: "2010+" },
    { value: 2015, label: "2015+" },
    { value: 2020, label: "2020+" },
    { value: new Date().getFullYear(), label: new Date().getFullYear() },
  ];

  const filterPriorityOptions = [
    { value: "None", label: "None" },
    { value: "Show agent listing first", label: "Show agent listing first" },
    { value: "Show office listing first", label: "Show office listing first" },
  ];

  const sortListingsOptions = [
    { value: { field: "price", order: "desc" }, label: "Price (highest to lowest)" },
    { value: { field: "price", order: "asc" }, label: "Price (lowest to highest)" },
    { value: { field: "listDate", order: "desc" }, label: "Date Listed (newest to oldest)" },
    { value: { field: "listDate", order: "asc" }, label: "Date Listed (oldest to newest)" },
    // { value: "Featured", label: "Featured" },
  ];

  const onAreaInputChanged = (e) => {
    setSearchTerm(e);
  };

  const onAreaSelected = (e) => {
    setSearch("");

    const splittedValue = e.value.split(",");

    switch (e.type) {
      case "address":
        updateConfig("streetAddress", splittedValue[0]);
        break;
      case "postalCode":
        updateConfig("postalCodes", splittedValue[0]?.trim());
        break;
      case "city":
        updateConfig("cityStates", { city: splittedValue[0]?.trim(), state: splittedValue[1]?.trim() });
        break;
      case "neighborhood":
        updateConfig("neighborhoodCityStates", {
          neighborhood: splittedValue[0]?.trim(),
          city: splittedValue[1]?.trim(),
          state: splittedValue[2]?.trim(),
        });
        break;
      case "schoolDistrict":
        break;
      case "county":
        updateConfig("countyStates", {
          county: splittedValue[0]?.trim(),
          state: splittedValue[1]?.trim(),
        });
        break;

      default:
        break;
    }
  };

  return (
    <CollapsibleWebsiteCard
      title="Featured Listings"
      disabled={!data?.isActive}
      action={
        <Toggle
          checked={data?.isActive}
          onChange={(e) => {
            setFeaturedListingsData({
              ...data,
              isActive: e.target.checked,
            });
          }}
        />
      }
    >
      <div className="tw-flex tw-gap-[24px] ">
        <div className="tw-flex tw-flex-col tw-flex-1 tw-gap-[24px]">
          {/* Areas */}
          <div className="tw-flex tw-flex-col tw-flex-1 tw-gap-[16px] tw-p-[16px] tw-border tw-border-solid tw-border-gray-10 tw-rounded-xl tw-max-h-[164px]">
            <Typography variant="subtitle-m-bold">Area</Typography>
            <div className="tw-flex tw-flex-col tw-gap-[8px]">
              <Dropdown
                components={{
                  DropdownIndicator: () => {},
                }}
                containerClassName="tw-flex-1"
                label="Search Areas"
                placeholder="Search for cities, ZIP/Postal codes, or neighborhoods"
                value={search}
                isClearable={false}
                isSearchable
                onChange={onAreaSelected}
                onInputChange={onAreaInputChanged}
                options={areaGroups}
              />

              <div className="tw-flex tw-gap-[8px]">
                {data?.bindings?.[0]?.config?.vars?.requestBody?.cityStates?.map((tag) => (
                  <Tag
                    key={tag.id}
                    removable
                    onRemove={() =>
                      updateConfig("cityStates", {
                        city: tag.city,
                        state: tag.state,
                      })
                    }
                  >
                    {`${tag.city}, ${tag.state}`}
                  </Tag>
                ))}
                {data?.bindings?.[0]?.config?.vars?.requestBody?.postalCodes?.map((tag) => (
                  <Tag key={tag.id} removable onRemove={() => updateConfig("postalCodes", tag)}>
                    {tag}
                  </Tag>
                ))}
                {data?.bindings?.[0]?.config?.vars?.requestBody?.streetAddress?.map((tag) => (
                  <Tag key={tag.id} removable onRemove={() => updateConfig("streetAddress", tag)}>
                    {tag}
                  </Tag>
                ))}
                {data?.bindings?.[0]?.config?.vars?.requestBody?.neighborhoodCityStates?.map((tag) => (
                  <Tag
                    key={tag.id}
                    removable
                    onRemove={() =>
                      updateConfig("neighborhoodCityStates", {
                        neighborhood: tag.neighborhood,
                        city: tag.city,
                        state: tag.state,
                      })
                    }
                  >
                    {`${tag.neighborhood}, ${tag.city}, ${tag.state}`}
                  </Tag>
                ))}
                {data?.bindings?.[0]?.config?.vars?.requestBody?.countyStates?.map((tag) => (
                  <Tag
                    key={tag.id}
                    removable
                    onRemove={() =>
                      updateConfig("countyStates", {
                        county: tag.county,
                        state: tag.state,
                      })
                    }
                  >
                    {`${tag.county}, ${tag.state}`}
                  </Tag>
                ))}
              </div>
            </div>
          </div>

          {/* Property Details */}
          <div className="tw-flex tw-flex-col tw-flex-1 tw-gap-[16px] tw-p-[16px] tw-border tw-border-solid tw-border-gray-10 tw-rounded-xl tw-max-h-[596px]">
            <Typography variant="subtitle-m-bold">Property Details</Typography>
            {/* Property Type  */}
            <div className="tw-flex tw-flex-col tw-gap-[4px]">
              <Typography variant="subtitle-m" className="!tw-text-gray-50">
                Property Type
              </Typography>
              <div className="tw-flex tw-gap-[12px] tw-flex-wrap">
                {propertyTypes.map((type) => (
                  <Checkbox
                    label={type.label}
                    onChange={() => {
                      updateConfig("propertyTypes", type.id);
                    }}
                    checked={data?.bindings?.[0]?.config?.vars?.requestBody?.propertyTypes?.includes(type.id)}
                  />
                ))}
              </div>
            </div>
            {/* Property Status */}
            <div className="tw-flex tw-flex-col tw-gap-[4px]">
              <Typography variant="subtitle-m" className="!tw-text-gray-50">
                Property Status
              </Typography>
              <div className="tw-flex tw-gap-[12px] tw-flex-wrap">
                {propertyStatus.map((type) => (
                  <Checkbox
                    label={type.label}
                    onChange={() => {
                      updateConfig("statuses", type.id);
                    }}
                    checked={data?.bindings?.[0]?.config?.vars?.requestBody?.statuses?.includes(type.id)}
                  />
                ))}
              </div>
            </div>
            {/* Price */}
            <div className="tw-flex tw-gap-[5px] tw-items-end">
              <TextInput
                label="Price"
                containerClassName="tw-flex-1"
                placeholder=""
                value={data?.bindings?.[0]?.config?.vars?.requestBody?.priceMin}
                onChange={(e) => updateConfig("priceMin", e.target.value)}
              />
              <Typography className="!tw-text-gray-50 tw-mb-[8px]">to</Typography>
              <TextInput
                label=""
                containerClassName="tw-flex-1"
                placeholder=""
                value={data?.bindings?.[0]?.config?.vars?.requestBody?.priceMax}
                onChange={(e) => updateConfig("priceMax", e.target.value)}
              />
            </div>
            {/* Beds / Baths */}
            <div className="tw-flex tw-gap-[24px]">
              <Dropdown
                label="Beds"
                options={bedOptions}
                containerClassName="tw-flex-1"
                value={bedOptions.find(
                  (o) => o.value === data?.bindings?.[0]?.config?.vars?.requestBody?.bedsMin,
                )}
                onChange={(e) => updateConfig("bedsMin", e.value)}
              />
              <Dropdown
                label="Baths"
                options={bathOptions}
                containerClassName="tw-flex-1"
                value={bathOptions.find(
                  (o) => o.value === data?.bindings?.[0]?.config?.vars?.requestBody?.bathsMin,
                )}
                onChange={(e) => updateConfig("bathsMin", e.value)}
              />
            </div>
            {/* SQFT / Garage */}
            <div className="tw-flex tw-gap-[24px]">
              <Dropdown
                label="SQFT."
                options={sqftOptions}
                containerClassName="tw-flex-1"
                value={sqftOptions.find(
                  (o) => o.value === data?.bindings?.[0]?.config?.vars?.requestBody?.sqFtMin,
                )}
                onChange={(e) => updateConfig("sqFtMin", e.value)}
              />
              <Dropdown
                label="Garage"
                options={garageOptions}
                containerClassName="tw-flex-1"
                value={garageOptions.find(
                  (o) => o.value === data?.bindings?.[0]?.config?.vars?.requestBody?.garageMin,
                )}
                onChange={(e) => updateConfig("garageMin", e.value)}
              />
            </div>
            {/* Lot / Year */}
            <div className="tw-flex tw-gap-[24px]">
              <Dropdown
                label="Lot Size (acres)"
                options={lotOptions}
                containerClassName="tw-flex-1"
                value={lotOptions.find(
                  (o) => o.value === data?.bindings?.[0]?.config?.vars?.requestBody?.lotSizeMin,
                )}
                onChange={(e) => updateConfig("lotSizeMin", e.value)}
              />
              <Dropdown
                label="Year Built"
                options={yearOptions}
                containerClassName="tw-flex-1"
                value={yearOptions.find(
                  (o) => o.value === data?.bindings?.[0]?.config?.vars?.requestBody?.yearMin,
                )}
                onChange={(e) => updateConfig("yearMin", e.value)}
              />
            </div>
          </div>
        </div>

        <div className="tw-flex tw-flex-col tw-flex-1 tw-gap-[24px]">
          {/* Agent/Office Details */}
          <div className="tw-flex tw-flex-col tw-flex-1 tw-gap-[16px] tw-p-[16px] tw-border tw-border-solid tw-border-gray-10 tw-rounded-xl tw-max-h-[392px]">
            <Typography variant="subtitle-m-bold">Agent & Office Details</Typography>
            <div className="tw-flex tw-flex-col tw-gap-[8px]">
              <div className="tw-flex tw-gap-[8px] tw-items-end">
                <Dropdown
                  components={{
                    DropdownIndicator: () => {},
                  }}
                  containerClassName="tw-flex-1"
                  label="Agent IDs"
                  placeholder="Enter ID or search by agent name"
                  value={roster?.find((o) => o.value === page.agentId)}
                  isClearable
                  isSearchable
                  options={roster}
                  onChange={(e) => {
                    setPage((prev) => ({
                      ...prev,
                      agentId: e?.value,
                    }));
                  }}
                  onInputChange={(e) => {
                    setPage((prev) => ({
                      ...prev,
                      agentId: e,
                    }));
                  }}
                />
                <Button
                  size="medium"
                  variant="primary"
                  label="ADD ID"
                  onClick={() => updateConfig("agentIds", page.agentId)}
                >
                  ADD ID
                </Button>
              </div>
              <div className="tw-flex tw-gap-[8px]">
                {data?.bindings?.[0]?.config?.vars?.requestBody?.agentIds?.map((tag) => (
                  <Tag key={tag} removable onRemove={() => updateConfig("agentIds", tag)}>
                    {tag}
                  </Tag>
                ))}
              </div>
            </div>

            <div className="tw-flex tw-flex-col tw-gap-[8px]">
              <div className="tw-flex tw-gap-[8px] tw-items-end">
                <TextInput
                  containerClassName="tw-flex-1"
                  label="Office IDs"
                  placeholder="Enter an MLS Office ID"
                  value={page.officeId}
                  onChange={(e) => setPage((prev) => ({ ...prev, officeId: e.target.value }))}
                />
                <Button
                  size="medium"
                  variant="primary"
                  label="ADD ID"
                  onClick={() => updateConfig("officeIds", page.officeId)}
                >
                  ADD ID
                </Button>
              </div>
              <div className="tw-flex tw-gap-[8px]">
                {data?.bindings?.[0]?.config?.vars?.requestBody?.officeIds?.map((tag) => (
                  <Tag key={tag} removable onRemove={() => updateConfig("officeIds", tag)}>
                    {tag}
                  </Tag>
                ))}
              </div>
            </div>

            <Dropdown
              label="Filter Priority"
              options={filterPriorityOptions}
              containerClassName="tw-flex-1"
              value={filterPriorityOptions.find((o) => o.value === page.filterPriority)}
              onChange={(e) =>
                setPage((prev) => ({
                  ...prev,
                  filterPriority: e.value,
                }))
              }
            />
            <Checkbox
              label="Only show agent/office listings"
              value={page.onlyShowAgentOfficeListings}
              onChange={(e) =>
                setPage((prev) => ({
                  ...prev,
                  onlyShowAgentOfficeListings: e.target.checked,
                }))
              }
            />
          </div>

          {/* Display Settings */}
          <div className="tw-flex tw-flex-col tw-flex-1 tw-gap-[16px] tw-p-[16px] tw-border tw-border-solid tw-border-gray-10 tw-rounded-xl tw-max-h-[268px]">
            <Typography variant="subtitle-m-bold">Display Settings</Typography>
            <Typography variant="subtitle-m" className="!tw-text-gray-50">
              MLS Details
            </Typography>
            <div className="tw-flex tw-gap-[12px] tw-flex-wrap">
              <Checkbox
                label="Beds"
                checked={data?.bindings?.[0]?.config?.vars?.requestBody?.queryOptions?.select?.includes(
                  "bedrooms",
                )}
                onChange={() => {
                  updateConfig("queryOptions.select", "bedrooms");
                }}
              />
              <Checkbox
                label="Baths"
                checked={data?.bindings?.[0]?.config?.vars?.requestBody?.queryOptions?.select?.includes(
                  "fullBaths",
                )}
                onChange={() => {
                  updateConfig("queryOptions.select", "fullBaths");
                }}
              />
              <Checkbox
                label="SQFT"
                checked={data?.bindings?.[0]?.config?.vars?.requestBody?.queryOptions?.select?.includes(
                  "squareFeet",
                )}
                onChange={() => {
                  updateConfig("queryOptions.select", "squareFeet");
                }}
              />
            </div>
            <div className="tw-flex tw-flex-col tw-gap-[12px]">
              <div className="tw-flex tw-gap-[4px] tw-items-center">
                <Checkbox
                  label="Only show new listings"
                  checked={
                    data?.bindings?.[0]?.config?.vars?.requestBody.listDate != null &&
                    Object.hasOwn(data?.bindings?.[0]?.config?.vars?.requestBody, "listDate")
                  }
                  onChange={(e) => {
                    if (e.target.checked) {
                      const today = dayjs().format("YYYY-MM-DD");
                      const oneWeekAgo = dayjs().subtract(1, "week").format("YYYY-MM-DD");

                      updateConfig("listDate.from", today);
                      updateConfig("listDate.to", oneWeekAgo);
                    } else {
                      updateConfig("listDate", null);
                    }
                  }}
                />
                <Tooltip
                  innerClassName="!tw-text-left tw-max-w-[160px]"
                  trigger={<CircleInfoSolidV6 />}
                  content="New listings are properties listed within the last 7 days"
                />
              </div>
              <Checkbox
                label="Only show listings with open houses"
                checked={data?.bindings?.[0]?.config?.vars?.requestBody?.requireOpenHouses}
                onChange={(e) => {
                  updateConfig("requireOpenHouses", e.target.checked);
                }}
              />
            </div>
            <Dropdown
              label="Sort Listings By"
              options={sortListingsOptions}
              containerClassName="tw-flex-1"
              value={sortListingsOptions.find(
                (o) =>
                  o.value.field ===
                    data?.bindings?.[0]?.config?.vars?.requestBody?.queryOptions?.sort?.[0]?.field &&
                  o.value.order ===
                    data?.bindings?.[0]?.config?.vars?.requestBody?.queryOptions?.sort?.[0]?.order,
              )}
              // The first sort entry is the primary ordering; a selection replaces it.
              onChange={(e) => updateConfig("queryOptions.sort", [e.value], { replace: true })}
            />
          </div>
        </div>
      </div>
    </CollapsibleWebsiteCard>
  );
};

const numberOrString = PropTypes.oneOfType([PropTypes.number, PropTypes.string]);

FeaturedListingsPanel.propTypes = {
  data: PropTypes.shape({
    isActive: PropTypes.bool,
    bindings: PropTypes.arrayOf(
      PropTypes.shape({
        uuid: PropTypes.string,
        config: PropTypes.shape({
          vars: PropTypes.shape({
            requestBody: PropTypes.shape({
              agentIds: PropTypes.arrayOf(numberOrString),
              bathsMin: numberOrString,
              bedsMin: numberOrString,
              cityStates: PropTypes.arrayOf(
                PropTypes.shape({ city: PropTypes.string, state: PropTypes.string }),
              ),
              countyStates: PropTypes.arrayOf(PropTypes.shape({})),
              garageMin: numberOrString,
              listDate: PropTypes.shape({ from: PropTypes.string, to: PropTypes.string }),
              lotSizeMin: numberOrString,
              neighborhoodCityStates: PropTypes.arrayOf(PropTypes.shape({})),
              officeIds: PropTypes.arrayOf(numberOrString),
              postalCodes: PropTypes.arrayOf(PropTypes.string),
              priceMax: numberOrString,
              priceMin: numberOrString,
              propertyTypes: PropTypes.arrayOf(PropTypes.string),
              queryOptions: PropTypes.shape({
                select: PropTypes.arrayOf(PropTypes.string),
                sort: PropTypes.arrayOf(
                  PropTypes.shape({ field: PropTypes.string, order: PropTypes.string }),
                ),
              }),
              requireOpenHouses: PropTypes.bool,
              sqFtMin: numberOrString,
              statuses: PropTypes.arrayOf(PropTypes.string),
              streetAddress: PropTypes.arrayOf(PropTypes.string),
              yearMin: numberOrString,
            }),
          }),
        }),
      }),
    ),
  }),
  setFeaturedListingsData: PropTypes.func.isRequired,
};

export default FeaturedListingsPanel;
